import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    destinationId: '',
    title: '',
    description: '',
    duration: '',
    price: '',
    originalPrice: '',
    image: '',
    inclusions: '',
    exclusions: '',
    itinerary: '',
    featured: false,
    active: true
  });

  useEffect(() => {
    fetchPackages();
    fetchDestinations();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/packages`);
      const data = await response.json();
      setPackages(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/destinations`);
      const data = await response.json();
      setDestinations(data);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const packageData = {
      ...formData,
      inclusions: formData.inclusions.split('\n').filter(i => i.trim()),
      exclusions: formData.exclusions ? formData.exclusions.split('\n').filter(i => i.trim()) : [],
      itinerary: formData.itinerary ? JSON.parse(formData.itinerary) : []
    };

    try {
      const url = editingPackage 
        ? `${API_URL}/api/admin/packages/${editingPackage.id}`
        : `${API_URL}/api/admin/packages`;
      
      const method = editingPackage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(packageData)
      });

      if (response.ok) {
        toast.success(editingPackage ? 'Package updated!' : 'Package created!');
        fetchPackages();
        closeModal();
      } else {
        toast.error('Failed to save package');
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Failed to save package');
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      destinationId: pkg.destinationId,
      title: pkg.title,
      description: pkg.description,
      duration: pkg.duration,
      price: pkg.price,
      originalPrice: pkg.originalPrice || '',
      image: pkg.image,
      inclusions: pkg.inclusions.join('\n'),
      exclusions: pkg.exclusions ? pkg.exclusions.join('\n') : '',
      itinerary: pkg.itinerary ? JSON.stringify(pkg.itinerary, null, 2) : '',
      featured: pkg.featured,
      active: pkg.active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/packages/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Package deleted!');
        fetchPackages();
      } else {
        toast.error('Failed to delete package');
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  const openNewModal = () => {
    setEditingPackage(null);
    setFormData({
      destinationId: '',
      title: '',
      description: '',
      duration: '',
      price: '',
      originalPrice: '',
      image: '',
      inclusions: '',
      exclusions: '',
      itinerary: '',
      featured: false,
      active: true
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPackage(null);
  };

  const getDestinationName = (destId) => {
    const dest = destinations.find(d => d.id === destId);
    return dest ? dest.name : destId;
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="admin-content">
      <div className="admin-header">
        <h1>Manage Packages</h1>
        <button onClick={openNewModal} className="admin-btn primary">
          <i className="fa-solid fa-plus"></i> Add Package
        </button>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Title</th>
              <th>Destination</th>
              <th>Duration</th>
              <th>Price</th>
              <th>Featured</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {packages.map(pkg => (
              <tr key={pkg.id}>
                <td>
                  <img src={pkg.image} alt={pkg.title} className="table-thumb" />
                </td>
                <td>{pkg.title}</td>
                <td>{getDestinationName(pkg.destinationId)}</td>
                <td>{pkg.duration}</td>
                <td>
                  <div className="price-cell">
                    <span className="current-price">${pkg.price}</span>
                    {pkg.originalPrice && (
                      <span className="original-price">${pkg.originalPrice}</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${pkg.featured ? 'featured' : ''}`}>
                    {pkg.featured ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${pkg.active ? 'active' : 'inactive'}`}>
                    {pkg.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button onClick={() => handleEdit(pkg)} className="admin-btn small">
                      <i className="fa-solid fa-edit"></i>
                    </button>
                    <button onClick={() => handleDelete(pkg.id)} className="admin-btn small danger">
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {packages.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="fa-solid fa-box-open"></i>
            </div>
            <h3>No Packages Yet</h3>
            <p>Start creating travel packages for your destinations</p>
            <button onClick={openNewModal} className="admin-btn primary">
              <i className="fa-solid fa-plus"></i> Create First Package
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal large" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingPackage ? 'Edit Package' : 'Add New Package'}</h2>
              <button onClick={closeModal} className="modal-close">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Destination *</label>
                  <select
                    name="destinationId"
                    value={formData.destinationId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Destination</option>
                    {destinations.map(dest => (
                      <option key={dest.id} value={dest.id}>{dest.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Duration *</label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="e.g., 5 Days 4 Nights"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Package title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Package description"
                  required
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="e.g., 1299"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Original Price</label>
                  <input
                    type="text"
                    name="originalPrice"
                    value={formData.originalPrice}
                    onChange={handleInputChange}
                    placeholder="e.g., 1599"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Image URL *</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Inclusions * (one per line)</label>
                <textarea
                  name="inclusions"
                  value={formData.inclusions}
                  onChange={handleInputChange}
                  rows="5"
                  placeholder="Flight tickets&#10;Hotel accommodation&#10;Breakfast"
                  required
                />
              </div>

              <div className="form-group">
                <label>Exclusions (one per line)</label>
                <textarea
                  name="exclusions"
                  value={formData.exclusions}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Lunch and dinner&#10;Personal expenses"
                />
              </div>

              <div className="form-group">
                <label>Itinerary (JSON format)</label>
                <textarea
                  name="itinerary"
                  value={formData.itinerary}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder='[{"day": 1, "title": "Arrival", "description": "..."}]'
                />
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  <span>Featured Package</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={closeModal} className="admin-btn secondary">
                  Cancel
                </button>
                <button type="submit" className="admin-btn primary">
                  {editingPackage ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
